import { FormattedMessage, useIntl } from 'react-intl';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box, Flex, PrimaryDialog, PrimaryDialogProps } from '@atoms/layout';
import { LoadingButton, Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { useReportIssueStore } from '@libs/store/reportIssue';
import { Stack, Tooltip } from '@mui/material';
import { useAuthStore } from '@libs/store/auth';
import { Checkbox, TextField } from '@atoms/inputs';
import { useState } from 'react';
import { useNotification } from '@libs/snackbar';
import { useOrganizationDetailsStore } from '@libs/store/settings';

type AssignDialogProps = Omit<PrimaryDialogProps, 'onSubmit'> & {
  onClose?: () => void;
};
export function ReportDialog({ onClose, ...props }: AssignDialogProps) {
  const intl = useIntl();

  const { showSuccessIntl, showErrorIntl } = useNotification();
  const { data: orgDetails, loading: orgDataLoading } = useOrganizationDetailsStore();
  const { userData, userDataLoading } = useAuthStore();
  const { closeDialog, submitting, sendIssueReport } = useReportIssueStore();
  const [issueDescription, setIssueDescription] = useState<string>('');
  const [collectLogs, setCollectLogs] = useState<boolean>(true);

  const loading = submitting || userDataLoading || orgDataLoading || !userData || !orgDetails;
  const localizationSubset = 'reportAnIssue';

  const handleDialogClose = () => {
    onClose && onClose();
    closeDialog();
  };

  const handleSubmit = async () => {
    if (userData && orgDetails && collectLogs && issueDescription) {
      try {
        await sendIssueReport(userData, orgDetails, issueDescription);
        showSuccessIntl(`${localizationSubset}.dialog.successMessage`);
      } catch (err) {
        showErrorIntl(`${localizationSubset}.dialog.errorMessage`);
      } finally {
        handleDialogClose();
      }
    }
  };

  return (
    <PrimaryDialog fullWidth maxWidth={'xs'} {...props}>
      <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
        <Flex justifyContent="space-between">
          <Flex column gap={1}>
            <Typography variant="h5">
              <FormattedMessage id={`${localizationSubset}.dialog.title`} />
            </Typography>
          </Flex>
          <Box>
            <IconButton sx={{ width: '40px', position: 'relative', top: -5, right: -5 }} onClick={handleDialogClose}>
              <FaIcon name="xmark" variant="light" />
            </IconButton>
          </Box>
        </Flex>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'center'}>
            <Typography variant="subtitle1" noWrap>
              <FormattedMessage id={`${localizationSubset}.dialog.reporterLabel`} />
            </Typography>

            <Typography variant="body1" noWrap>
              {`${userData?.firstName} ${userData?.lastName} (${userData?.email})`}
            </Typography>
          </Stack>
          {collectLogs && (
            <Typography variant="body2" color="warning.main">
              <FormattedMessage id={`${localizationSubset}.dialog.logGrabbedMessage`} />
            </Typography>
          )}

          <TextField
            fullWidth
            multiline
            onChange={(evt) => {
              setIssueDescription(evt.target.value);
            }}
            rows={4}
            label={intl.formatMessage({ id: `${localizationSubset}.dialog.issueDescriptionLabel` })}
          />
        </Stack>
        <Tooltip
          title={<FormattedMessage id={`${localizationSubset}.dialog.logGrabbedTooltip`} />}
          arrow
          placement="top"
        >
          <Stack direction="row" spacing={0.1} alignItems={'center'}>
            <Checkbox checked={collectLogs} onChange={(evt, checked) => setCollectLogs(checked)} />
            <Typography variant="body2">
              <FormattedMessage id={`${localizationSubset}.dialog.logGrabbedCheckboxLabel`} />{' '}
              <FaIcon name="circle-info" />
            </Typography>
          </Stack>
        </Tooltip>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box color="text.secondary">
          <Button variant="text" color="inherit" rounded onClick={handleDialogClose} size="large">
            <FormattedMessage id={`${localizationSubset}.dialog.actions.cancel`} />
          </Button>
        </Box>
        <LoadingButton
          loading={loading}
          disabled={!collectLogs || !issueDescription}
          variant="contained"
          rounded
          type="submit"
          size="large"
          onClick={handleSubmit}
        >
          <FormattedMessage id={`${localizationSubset}.dialog.actions.submit`} />
        </LoadingButton>
      </DialogActions>
    </PrimaryDialog>
  );
}
